import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Breadcrums from "../../common/breadcrumbs";
import Loader from "../../common/loader";
import {
    globalLoader,
    handleServerValidations,
} from "../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { Update, Details } from "../../services/puja.services";
import { List as CategoryList } from "../../services/pujaCategory.services";
// import { List as TypeList } from "../../services/pujaType.services";

import Select from "react-select";
import CustomCkeditor from "../../common/customeditor";


const EditPuja = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();
    const [submitted, setSubmitted] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [existingImage, setExistingImage] = useState(null);
    const [categories, setCategories] = useState([]);
    const [pujaTypes, setPujaTypes] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [initialData, setInitialData] = useState({
        title: "",
        price: "",
        price_usd: "",
        time: "",
        benefits: "",
        description: "",
        about_description: "",
        required_materials: "",
        jaap_count: "",
        ahuti_count: "",
        manter: "",
        status: 1,
        image: null,
        categories: [],
        puja_types: [],
        puja_mode: "",
    });

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "Pujas", url: "/admin/puja/list/1" },
        { title: t("btn_edit"), url: "" },
    ];

    useEffect(() => {
        const fetchDropdowns = async () => {
            const formData = new FormData();
            formData.append("page", 1);
            formData.append("per_page", 1000);
            formData.append("search", JSON.stringify({ type: "puja" }));

            try {
                const catRes = await CategoryList(formData);
                const activeCategories = (catRes?.data?.list || []).filter(c => c.status === 1 || c.status === true);
                setCategories(activeCategories);
                return { cats: activeCategories, types: [] };
            } catch (err) {
                console.error("Error fetching categories:", err);
                return { cats: [], types: [] };
            }
        };

        if (id) {
            globalLoader(true);
            fetchDropdowns().then(({ cats, types }) => {
                Details(id)
                    .then((response) => {
                        if (response.success) {
                            const data = response.data;

                            // Map existing IDs to actual objects for Select
                            const selectedCats = cats.filter(c => data.PujaCategories?.some(pc => pc.id === c.id)).map(c => ({ label: c.title, value: c.id }));
                            const selectedTypes = types.filter(t => data.PujaTypes?.some(pt => pt.id === t.id)).map(t => ({ label: t.title, value: t.id }));

                            setInitialData({
                                title: data.title || "",
                                price: data.price || "",
                                price_usd: data.price_usd || "",
                                time: data.time || "",
                                benefits: data.benefits || "",
                                description: data.description || "",
                                about_description: data.about_description || "",
                                required_materials: data.required_materials || "",
                                jaap_count: data.jaap_count || "",
                                ahuti_count: data.ahuti_count || "",
                                manter: data.manter || "",
                                status: (data.status === true || data.status === 1) ? 1 : 0,
                                image: null,
                                categories: selectedCats,
                                puja_types: selectedTypes,
                                puja_mode: data.puja_mode || "online",
                                gst_percent: data.gst_percent || 0,
                            });

                            if (data.image) {
                                const imgUrl = data.image.startsWith("http")
                                    ? data.image
                                    : `${process.env.REACT_APP_API_URL || ""}/${data.image}`;
                                setExistingImage(imgUrl);
                            }
                        }
                        setIsLoaded(true);
                        globalLoader(false);
                    })
                    .catch((error) => {
                        console.log("error fetching details", error);
                        setIsLoaded(true);
                        globalLoader(false);
                    });
            });
        }
    }, [id]);

    const validationSchema = Yup.object().shape({
        title: Yup.string().trim().required("Title is required").max(255, "Title must be at most 255 characters"),
        price: Yup.number().typeError("Price must be a number").required("Price is required").min(0, "Price cannot be negative"),
        price_usd: Yup.number().typeError("USD Price must be a number").required("USD Price is required").min(0, "USD Price cannot be negative"),
        time: Yup.number()
            .transform((value, originalValue) => (originalValue === "" || originalValue === null ? null : Number(originalValue)))
            .nullable()
            .optional()
            .typeError("Time must be a number"),
        description: Yup.string().trim().required("Description is required"),
        benefits: Yup.string().trim().required("Benefits are required"),
        required_materials: Yup.string().optional(),
        categories: Yup.array().min(1, "Select at least one category"),
        // puja_types: Yup.array().min(1, "Select at least one puja type"),
        puja_mode: Yup.string().required("Puja mode is required"),
        jaap_count: Yup.string()
            .optional()
            .nullable()
            .matches(/^\d*$/, "Jaap count must be numbers only"),
        ahuti_count: Yup.string()
            .optional()
            .nullable()
            .matches(/^\d*$/, "Ahuti count must be numbers only"),
        manter: Yup.string().optional(),
        gst_percent: Yup.number().typeError("GST percent must be a number").optional().min(0, "GST percent cannot be negative").max(100, "GST percent cannot exceed 100"),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialData,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            globalLoader(true);
            setSubmitted(true);

            const formData = new FormData();
            formData.append("id", id);
            formData.append("title", values.title);
            formData.append("price", values.price);
            formData.append("price_usd", values.price_usd);
            formData.append("time", values.time || "");
            formData.append("benefits", values.benefits);
            formData.append("description", values.description);
            formData.append("about_description", values.about_description || "");
            formData.append("required_materials", values.required_materials);
            formData.append("jaap_count", values.jaap_count || "");
            formData.append("ahuti_count", values.ahuti_count || "");
            formData.append("manter", values.manter || "");
            formData.append("status", values.status);
            formData.append("categories", JSON.stringify(values.categories.map(c => c.value)));
            formData.append("puja_types", JSON.stringify(values.puja_types.map(t => t.value)));
            formData.append("puja_mode", values.puja_mode);
            formData.append("gst_percent", values.gst_percent || 0);

            if (values.image) {
                formData.append("image", values.image);
            }

            Update(formData)
                .then((response) => {
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setTimeout(() => {
                            navigate(`/admin/puja/list/1`);
                        }, 1000);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setSubmitted(false);
                    }
                })
                .catch((error) => {
                    console.log("error ====> ", error);
                    globalLoader(false);
                    setSubmitted(false);
                });
        },
    });

    const handleSubmitForm = (e) => {
        e.preventDefault();
        formik.handleSubmit(e);
        setTimeout(() => {
            if (Object.keys(formik.errors).length > 0) {
                const firstErrorKey = Object.keys(formik.errors)[0];
                const firstErrorMsg = formik.errors[firstErrorKey];
                Swal.fire({
                    icon: "warning",
                    text: typeof firstErrorMsg === "string" ? firstErrorMsg : "Please fill out all required fields",
                    ...SWAL_SETTINGS,
                });
            }
        }, 100);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            formik.setFieldValue("image", file);
            const reader = new FileReader();
            reader.onload = (ev) => setPreviewImage(ev.target.result);
            reader.readAsDataURL(file);
        }
    };

    const currentImageSrc = previewImage || existingImage;

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <h6 className="main-content-label mb-3">Edit Puja</h6>
                        {isLoaded ? (
                            <form onSubmit={handleSubmitForm}>
                            <div className="row row-sm">
                                {/* Title */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="title" className="text-left d-flex">
                                        Title :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="title"
                                        id="title"
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter puja title"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.title}
                                    />
                                    {formik.errors.title && formik.touched.title && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.title}
                                        </span>
                                    )}
                                </div>

                                {/* Price */}
                                <div className="col-lg-3 form-group">
                                    <label htmlFor="price" className="text-left d-flex">
                                        Price :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="price"
                                        id="price"
                                        type="number"
                                        className="form-control"
                                        placeholder="0.00"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.price}
                                        onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                                    />
                                    {formik.errors.price && formik.touched.price && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.price}
                                        </span>
                                    )}
                                </div>

                                {/* Price USD */}
                                <div className="col-lg-3 form-group">
                                    <label htmlFor="price_usd" className="text-left d-flex">
                                        Price (USD) :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="price_usd"
                                        id="price_usd"
                                        type="number"
                                        className="form-control"
                                        placeholder="0.00"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.price_usd}
                                        onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                                    />
                                    {formik.errors.price_usd && formik.touched.price_usd && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.price_usd}
                                        </span>
                                    )}
                                </div>

                                {/* Time */}
                                <div className="col-lg-3 form-group">
                                    <label htmlFor="time" className="text-left d-flex">
                                        Time (Minutes) :
                                    </label>
                                    <input
                                        name="time"
                                        id="time"
                                        type="number"
                                        className="form-control"
                                        placeholder="e.g. 60"
                                        min="5"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.time}
                                        onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                                    />
                                    {formik.errors.time && formik.touched.time && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.time}
                                        </span>
                                    )}
                                </div>

                                {/* GST Percent */}
                                <div className="col-lg-3 form-group">
                                    <label htmlFor="gst_percent" className="text-left d-flex">
                                        GST (%) (India Only) :
                                    </label>
                                    <input
                                        name="gst_percent"
                                        id="gst_percent"
                                        type="number"
                                        className="form-control"
                                        placeholder="0"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.gst_percent}
                                    />
                                    {formik.errors.gst_percent && formik.touched.gst_percent && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.gst_percent}
                                        </span>
                                    )}
                                </div>

                                {/* Status */}
                                <div className="col-lg-3 form-group">
                                    <label htmlFor="status" className="text-left d-flex">
                                        Status :
                                    </label>
                                    <select
                                        name="status"
                                        id="status"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.status}
                                    >
                                        <option value={1}>{t("Active")}</option>
                                        <option value={0}>{t("Inactive")}</option>
                                    </select>
                                </div>

                                {/* Puja Mode */}
                                <div className="col-lg-3 form-group">
                                    <label htmlFor="puja_mode" className="text-left d-flex">
                                        Puja Mode :<span className="requirestar">*</span>
                                    </label>
                                    <select
                                        name="puja_mode"
                                        id="puja_mode"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.puja_mode}
                                    >
                                        <option value="online">Online</option>
                                        <option value="in_person">In Person</option>
                                        <option value="both">Both</option>
                                    </select>
                                    {formik.errors.puja_mode && formik.touched.puja_mode && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.puja_mode}
                                        </span>
                                    )}
                                </div>

                                {/* Image */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="image" className="text-left d-flex">
                                        Puja Image :
                                    </label>
                                    <input
                                        name="image"
                                        id="image"
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                    />
                                    {currentImageSrc && (
                                        <div className="mt-2 text-left">
                                            <img
                                                src={currentImageSrc}
                                                alt="Current"
                                                style={{
                                                    width: "100px",
                                                    height: "100px",
                                                    objectFit: "cover",
                                                    borderRadius: "10px",
                                                    border: "1px solid #ddd",
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Categories */}
                                <div className="col-lg-6 form-group">
                                    <label className="text-left d-flex">Categories :<span className="requirestar">*</span></label>
                                    <Select
                                        isMulti
                                        options={categories.map(c => ({ label: c.title, value: c.id }))}
                                        value={formik.values.categories}
                                        onChange={(selectedOptions) => formik.setFieldValue("categories", selectedOptions)}
                                        placeholder="Select Categories"
                                        classNamePrefix="select"
                                    />
                                    {formik.errors.categories && formik.touched.categories && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.categories}
                                        </span>
                                    )}
                                </div>

                                {/* Puja Types */}
                                {/* <div className="col-lg-6 form-group">
                                    <label className="text-left d-flex">Puja Types :<span className="requirestar">*</span></label>
                                    <Select
                                        isMulti
                                        options={pujaTypes.map(t => ({ label: t.title, value: t.id }))}
                                        value={formik.values.puja_types}
                                        onChange={(selectedOptions) => formik.setFieldValue("puja_types", selectedOptions)}
                                        placeholder="Select Puja Types"
                                        classNamePrefix="select"
                                    />
                                    {formik.errors.puja_types && formik.touched.puja_types && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.puja_types}
                                        </span>
                                    )}
                                </div> */}

                                {/* Benefits */}
                                <div className="col-lg-12 form-group">
                                    <label htmlFor="benefits" className="text-left d-flex">
                                        Benefits :<span className="requirestar">*</span>
                                    </label>
                                    <textarea
                                        name="benefits"
                                        id="benefits"
                                        className="form-control"
                                        rows="3"
                                        placeholder="Enter puja benefits"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.benefits}
                                    ></textarea>
                                    {formik.errors.benefits && formik.touched.benefits && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.benefits}
                                        </span>
                                    )}
                                </div>

                                {/* Description */}
                                <div className="col-lg-12 form-group">
                                    <label htmlFor="description" className="text-left d-flex">
                                        Description :<span className="requirestar">*</span>
                                    </label>
                                    <CustomCkeditor
                                        fieldname="description"
                                        setFieldValue={formik.setFieldValue}
                                        value={formik.values.description}
                                        setFieldTouched={formik.setFieldTouched}
                                    />
                                    {formik.errors.description && formik.touched.description && (
                                        <span className="text-danger d-flex text-left mt-1">
                                            {formik.errors.description}
                                        </span>
                                    )}
                                </div>

                                {/* About / Detail Description (Without Editor) */}
                                <div className="col-lg-12 form-group">
                                    <label htmlFor="about_description" className="text-left d-flex">
                                        Detail Description (Plain Text) :
                                    </label>
                                    <textarea
                                        name="about_description"
                                        id="about_description"
                                        className="form-control"
                                        rows="4"
                                        placeholder="Enter detail description (without editor)"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.about_description}
                                    ></textarea>
                                </div>

                                {/* Required Materials */}
                                <div className="col-lg-12 form-group">
                                    <label htmlFor="required_materials" className="text-left d-flex">
                                        Required Materials :
                                    </label>
                                    <textarea
                                        name="required_materials"
                                        id="required_materials"
                                        className="form-control"
                                        rows="3"
                                        placeholder="Enter required materials"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.required_materials}
                                    ></textarea>
                                    {formik.errors.required_materials && formik.touched.required_materials && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.required_materials}
                                        </span>
                                    )}
                                </div>

                                {/* Jaap Count */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="jaap_count" className="text-left d-flex">
                                        Jaap Count :
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        name="jaap_count"
                                        id="jaap_count"
                                        className="form-control"
                                        placeholder="Enter jaap count (e.g. 108, 1008, 11000)"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.jaap_count}
                                    />
                                    {formik.errors.jaap_count && formik.touched.jaap_count && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.jaap_count}
                                        </span>
                                    )}
                                </div>

                                {/* Ahuti Count */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="ahuti_count" className="text-left d-flex">
                                        Ahuti Count :
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        name="ahuti_count"
                                        id="ahuti_count"
                                        className="form-control"
                                        placeholder="Enter ahuti count (e.g. 108, 1008)"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.ahuti_count}
                                    />
                                    {formik.errors.ahuti_count && formik.touched.ahuti_count && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.ahuti_count}
                                        </span>
                                    )}
                                </div>

                                {/* Mantra */}
                                <div className="col-lg-12 form-group">
                                    <label htmlFor="manter" className="text-left d-flex">
                                        Mantra :
                                    </label>
                                    <textarea
                                        name="manter"
                                        id="manter"
                                        className="form-control"
                                        rows="3"
                                        placeholder="Enter sacred mantra chant text"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.manter}
                                    ></textarea>
                                </div>

                                <div className="col-md-12">
                                    <button
                                        className="btn btn-main-primary signbtn mr-2"
                                        type="submit"
                                        disabled={submitted}
                                    >
                                        <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                        {t("btn_update")}
                                    </button>
                                    <button
                                        className="btn ripple btn-secondary"
                                        type="button"
                                        onClick={() => navigate(-1)}
                                    >
                                        <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                                        {t("btn_cancel")}
                                    </button>
                                </div>
                            </div>
                        </form>
                        ) : (
                            <Loader />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};


export default EditPuja;
